import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Interruptor } from "../../components/ui/primitivos";
import {
  type CatalogoItem,
  type Cliente,
  crearOrden,
  fetchCatalogo,
  fetchClientes,
} from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";

/**
 * Alta de una OT sin presupuesto — porteada de `ProduccionForm.tsx` (ADR 0053).
 *
 * Las cinco secciones de allá, con sus mismos campos: básica, fechas,
 * prioridad, horas estimadas y comercial. Lo que **no** se portea es la
 * validación del navegador como única defensa — la misma regla la aplica el
 * endpoint, y esto solo evita el viaje.
 */

const PRIORIDADES = ["BAJA", "NORMAL", "ALTA", "URGENTE"] as const;

/** Un bloque del formulario, con el mismo agrupado que `imprenta`. */
function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Card className="space-y-4 p-4">
      <h2 className="font-semibold text-neutral-900 text-sm">{titulo}</h2>
      {children}
    </Card>
  );
}

export function NuevaOtPagina() {
  const navegar = useNavigate();
  const clientes = useDatos(fetchClientes);
  const tiposTrabajo = useDatos(() => fetchCatalogo("tipos-trabajo"));

  const [clienteId, setClienteId] = useState("");
  const [producto, setProducto] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [tipoTrabajoId, setTipoTrabajoId] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [fechaInicioImpresion, setFechaInicioImpresion] = useState("");
  const [fechaPactadaEntrega, setFechaPactadaEntrega] = useState("");
  const [prioridad, setPrioridad] = useState<(typeof PRIORIDADES)[number]>("NORMAL");
  const [fechaLimite, setFechaLimite] = useState("");
  const [esUrgente, setEsUrgente] = useState(false);
  const [hsEstimMin, setHsEstimMin] = useState("");
  const [hsEstimMax, setHsEstimMax] = useState("");
  const [precioUnitario, setPrecioUnitario] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      // Los vacíos se mandan como `undefined` y no como cadena vacía: el
      // servidor los trata igual, pero el payload dice lo que quiere decir.
      const opcional = (v: string) => (v.trim() === "" ? undefined : v);
      const numero = (v: string) => (v.trim() === "" ? undefined : Number(v));

      const creada = await crearOrden({
        clienteId,
        producto,
        cantidad: Number(cantidad),
        tipoTrabajoId: opcional(tipoTrabajoId),
        observaciones: opcional(observaciones),
        fechaInicioImpresion: opcional(fechaInicioImpresion),
        fechaPactadaEntrega: opcional(fechaPactadaEntrega),
        prioridad,
        esUrgente,
        fechaLimite: opcional(fechaLimite),
        hsEstimMin: numero(hsEstimMin),
        hsEstimMax: numero(hsEstimMax),
        precioUnitario: numero(precioUnitario),
      });
      navegar(`/produccion?nueva=${encodeURIComponent(creada.numero)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear la orden");
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <PageHeader
        titulo="Nueva orden de trabajo"
        subtitulo="Para el trabajo que arranca sin presupuesto previo"
        // Flecha de volver al tablero, como el `showBackButton` de allá.
        alVolver={() => navegar("/produccion")}
        acciones={
          <Boton type="submit" disabled={guardando}>
            {guardando ? "Creando…" : "Crear orden"}
          </Boton>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {clientes.error ? <Aviso>{clientes.error}</Aviso> : null}

      <Seccion titulo="Información básica">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="cliente" className="block text-neutral-600 text-sm">
              Cliente *
            </label>
            <select
              id="cliente"
              required
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
            >
              <option value="">Seleccioná un cliente…</option>
              {(clientes.datos ?? []).map((c: Cliente) => (
                <option key={c.id} value={c.id}>
                  {c.razonSocial}
                  {c.nombreFantasia && c.nombreFantasia !== c.razonSocial
                    ? ` (${c.nombreFantasia})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="tipoTrabajo" className="block text-neutral-600 text-sm">
              Tipo de trabajo
            </label>
            <select
              id="tipoTrabajo"
              value={tipoTrabajoId}
              onChange={(e) => setTipoTrabajoId(e.target.value)}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
            >
              <option value="">Sin especificar</option>
              {(tiposTrabajo.datos ?? []).map((t: CatalogoItem) => (
                <option key={t.id} value={t.id}>
                  {(t.descripcion as string) ?? t.id}
                </option>
              ))}
            </select>
          </div>

          <Campo
            etiqueta="Producto *"
            required
            placeholder="Ej. Tarjetas personales, volantes…"
            value={producto}
            onChange={(e) => setProducto(e.target.value)}
          />
          <Campo
            etiqueta="Cantidad *"
            required
            type="number"
            min={1}
            step={1}
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="observaciones" className="block text-neutral-600 text-sm">
            Observaciones
          </label>
          <textarea
            id="observaciones"
            rows={3}
            maxLength={500}
            placeholder="Notas adicionales sobre la orden…"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
          />
        </div>
      </Seccion>

      <Seccion titulo="Fechas">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Fecha de inicio de impresión"
            type="date"
            value={fechaInicioImpresion}
            onChange={(e) => setFechaInicioImpresion(e.target.value)}
          />
          <Campo
            etiqueta="Fecha pactada de entrega"
            type="date"
            value={fechaPactadaEntrega}
            onChange={(e) => setFechaPactadaEntrega(e.target.value)}
            ayuda="Es la que agrupa el calendario de entregas"
          />
        </div>
      </Seccion>

      <Seccion titulo="Prioridad">
        <div className="grid items-end gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <label htmlFor="prioridad" className="block text-neutral-600 text-sm">
              Nivel de prioridad
            </label>
            <select
              id="prioridad"
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value as (typeof PRIORIDADES)[number])}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0) + p.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>
          <Campo
            etiqueta="Fecha límite"
            type="date"
            value={fechaLimite}
            onChange={(e) => setFechaLimite(e.target.value)}
          />
          <div className="flex items-center gap-2 pb-2">
            <Interruptor
              checked={esUrgente}
              onCheckedChange={setEsUrgente}
              aria-label="Marcar como urgente"
            />
            <span className="text-neutral-700 text-sm">Urgente</span>
          </div>
        </div>
      </Seccion>

      <Seccion titulo="Horas estimadas">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Horas mínimas"
            type="number"
            min={0}
            step="0.5"
            value={hsEstimMin}
            onChange={(e) => setHsEstimMin(e.target.value)}
          />
          <Campo
            etiqueta="Horas máximas"
            type="number"
            min={0}
            step="0.5"
            value={hsEstimMax}
            onChange={(e) => setHsEstimMax(e.target.value)}
          />
        </div>
      </Seccion>

      <Seccion titulo="Información comercial">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Precio unitario"
            type="number"
            min={0}
            step="0.01"
            value={precioUnitario}
            onChange={(e) => setPrecioUnitario(e.target.value)}
            ayuda="El total lo calcula el servidor: precio por cantidad"
          />
        </div>
      </Seccion>
    </form>
  );
}
